import { useEffect, useCallback, useState } from "react";
import { IPrimaryUser } from "services/api/api.types";
import { getPastSessionsForUser } from "services/api/sessions/sessionsService";
import {
  ClassSession,
  IPastSessionsAPIData
} from "services/api/sessions/sessionService.types";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import { useLiveClasses } from "./use-live-classes";
import * as Sentry from "@sentry/react";

export const usePastClasses = (
  primaryUser: IPrimaryUser
): {
  pastSessions: ClassSession[] | undefined;
  mediaBaseURL: string | undefined;
  currentTimeStamp: string | undefined;
  isPastSessionsLoading: boolean;
} => {
  const [isPastSessionsLoading, setPastSessionsLoading] = useBoolean();
  const [pastSessionsData, setPastSessionsData] =
    useState<IPastSessionsAPIData | null>(null);
  const { focusExamIds } = useLiveClasses(primaryUser);

  const fetchPastSessionsForUser = useCallback(
    async (focusExamIds: string[] | null | undefined) => {
      try {
        setPastSessionsLoading.on();
        const response = await getPastSessionsForUser(
          primaryUser.access_token,
          focusExamIds,
          100
        );
        setPastSessionsData(response.data);
        setPastSessionsLoading.off();
      } catch (err) {
        setPastSessionsLoading.off();
        Sentry.captureException(err);
      }
    },
    [primaryUser.access_token]
  );

  const fetchPastSessions = useCallback(
    async (focusExamIds: string[] | null | undefined) => {
      try {
        fetchPastSessionsForUser(focusExamIds);
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    [fetchPastSessionsForUser]
  );

  useEffect(() => {
    fetchPastSessions(focusExamIds);
  }, [fetchPastSessions, primaryUser.access_token, focusExamIds]);

  return {
    pastSessions: pastSessionsData?.sessions,
    mediaBaseURL: pastSessionsData?.mediaBaseUrl,
    currentTimeStamp: pastSessionsData?.currentTimeStamp,
    isPastSessionsLoading
  };
};
